import axiosInstance from './axiosInstance';

/**
 * Organization Service
 * Handles all organization related API calls
 */

const organizationService = {
  /**
   * Create a new organization
   * @param {Object} organizationData
   * @returns {Promise}
   */
  createOrganization: async (organizationData) => {
    try {
      const response = await axiosInstance.post(
        '/admin/organizations',
        organizationData
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Get all organizations
   * @param {Object} params - query parameters
   * @returns {Promise}
   */
  getAllOrganizations: async (params = {}) => {
    try {
      const response = await axiosInstance.get('/admin/organizations', {
        params,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Get organization by ID
   * @param {number} id
   * @returns {Promise}
   */
  getOrganizationById: async (id) => {
    try {
      const response = await axiosInstance.get(`/admin/organizations/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Update organization
   * @param {number} id
   * @param {Object} updateData
   * @returns {Promise}
   */
  updateOrganization: async (id, updateData) => {
    try {
      const response = await axiosInstance.put(
        `/admin/organizations/${id}`,
        updateData
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Update an organization's lifecycle status (Setup/Trial/Active/...)
   * @param {number} id
   * @param {string} status
   * @returns {Promise}
   */
  updateOrganizationStatus: async (id, status) => {
    try {
      const response = await axiosInstance.patch(
        `/admin/organizations/${id}/status`,
        { status }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Delete organization
   * @param {number} id
   * @returns {Promise}
   */
  deleteOrganization: async (id) => {
    try {
      const response = await axiosInstance.delete(`/admin/organizations/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Get organization statistics
   * @param {number} id
   * @returns {Promise}
   */
  getOrganizationStats: async (id) => {
    try {
      const response = await axiosInstance.get(
        `/admin/organizations/${id}/stats`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  /**
   * Get system-wide dashboard stats (organizations, service users, staff)
   * @returns {Promise}
   */
  getDashboardStats: async () => {
    try {
      const response = await axiosInstance.get('/admin/organizations/stats/summary');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Get all organization subscription plans
   * @param {Object} params - query parameters
   * @returns {Promise}
   */
  getAllOrganizationPlans: async (params = {}) => {
    try {
      const response = await axiosInstance.get('/admin/organizations/subscription-plans/all', {
        params,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Update a subscription plan's status (active/trial) directly from the
   * Subscription Plans screen.
   * @param {number} id - organization_plans.id
   * @param {string} subscription_status - 'active' | 'trial'
   * @returns {Promise}
   */
  updateSubscriptionStatus: async (id, subscription_status) => {
    try {
      const response = await axiosInstance.patch(
        `/admin/organizations/subscription-plans/${id}/status`,
        { subscription_status }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Get the next available Staff Login ID for a sector.
   * Care Home → CH1001, CH1002, ... ; Domiciliary → HC1001, HC1002, ...
   * Pass `offset` to pre-allocate sequential IDs when adding multiple managers
   * in the same wizard session (e.g. offset=1 for the 2nd new manager).
   */
  getNextUserId: async (type, offset = 0) => {
    try {
      const response = await axiosInstance.get('/admin/organizations/next-user-id', {
        params: { type, offset },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
};

export default organizationService;
